import React from 'react'
import { Calendar, CheckCircle2, RefreshCw, Clock, Ban } from 'lucide-react'

/**
 * AppointmentActions — the three things a patient can do with a booked visit.
 *
 * v242.69.0 (CASEHOME-APPT-ACTIONS-1). The appointment card used to be one big
 * navigate-to-/visits target with a 28px unlabelled RefreshCw tucked beside the
 * status pill. Reschedule was reachable but unreadable, and check-in was not
 * reachable from Case Home at all — the chat told the patient to "Tap Diagnose
 * to check in", which is an instruction, not a control.
 *
 * `eligible` is NOT computed here on purpose. It comes from
 * appointmentWindow.isCheckInEligible so this component cannot become a fourth
 * definition of the 24-hour window.
 *
 * `showCheckIn=false` is for the follow-up card. Follow-up check-in is NOT a
 * verified flow in this build — DiagnoseScreen's own gate admits only
 * claimed / in_progress / scheduled, and a follow-up encounter does not reach
 * those. Rendering a permanently-greyed button under "opens 24 hours before
 * your visit" would promise a control that never arrives, so the follow-up card
 * offers View and Change only (CASEHOME-FOLLOWUP-CHECKIN-1).
 *
 * When the window is shut the button stays VISIBLE and disabled rather than
 * disappearing, with the reason underneath. A control that vanishes teaches the
 * patient it does not exist; a control that is greyed with "opens 24 hours
 * before" teaches them when to come back.
 */
export default function AppointmentActions({
  eligible = false,
  checkedIn = false,
  showCheckIn = true,
  onView,
  onCheckIn,
  onChange,
  onCancel,
  canChange = true,
  labels = {},
}) {
  const L = {
    view: 'View',
    checkIn: 'Check in',
    checkedIn: 'Checked in',
    change: 'Change',
    cancel: 'Cancel & withdraw case',
    closedNote: 'Check-in opens 24 hours before your visit',
    ...labels,
  }

  const base = {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    borderRadius: 999,
    padding: '9px 8px',
    fontWeight: 700,
    fontSize: 11.5,
    whiteSpace: 'nowrap',
    border: '1.5px solid #E8EDF2',
    background: '#FFFFFF',
    color: '#15354F',
  }
  const primary = { ...base, background: '#15354F', borderColor: '#15354F', color: '#FFFFFF' }
  const off = { ...base, background: '#F5F8FA', borderColor: '#E8EDF2', color: '#A6B4C2', cursor: 'default' }

  const checkInDisabled = !eligible || checkedIn
  const checkInStyle = checkedIn ? base : eligible ? primary : off
  const checkInIconColor = checkedIn ? '#1B873A' : eligible ? '#FFFFFF' : '#C3CEDA'

  return (
    <div style={{ paddingTop: 11, marginTop: 2, borderTop: '1px solid #F1F5F8' }}>
      <div style={{ display: 'flex', gap: 7 }}>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onView?.() }}
          style={base}
          className="active:scale-[0.97] transition-transform"
        >
          <Calendar size={13} style={{ color: '#46A8F9' }} />
          {L.view}
        </button>

        {showCheckIn && (
        <button
          type="button"
          disabled={checkInDisabled}
          onClick={(e) => { e.stopPropagation(); if (!checkInDisabled) onCheckIn?.() }}
          style={checkInStyle}
          /* breathe-navy only while the window is actually open — a control that
             pulses when it cannot be pressed is the same lie as a red countdown
             on a screen that is not counting down. */
          className={`transition-transform ${eligible && !checkedIn ? 'breathe-navy active:scale-[0.97]' : ''}`}
        >
          <CheckCircle2 size={13} style={{ color: checkInIconColor }} />
          {checkedIn ? L.checkedIn : L.checkIn}
        </button>
        )}

        <button
          type="button"
          disabled={!canChange}
          onClick={(e) => { e.stopPropagation(); if (canChange) onChange?.() }}
          style={canChange ? base : off}
          className={canChange ? 'active:scale-[0.97] transition-transform' : ''}
        >
          <RefreshCw size={13} style={{ color: canChange ? '#46A8F9' : '#C3CEDA' }} />
          {L.change}
        </button>

        {/* ★★ v242.107.0 — CANCEL IS THE FOURTH PILL, NOT A FOURTH LABEL.
            Dr. Rami chose the outlined treatment from three mocks: red glyph,
            red hairline, WHITE field. Tinting it would have made the
            destructive control the brightest thing in the row — louder than
            Check in, which is the action the card actually wants tapped.
            ★★ `flex: 0 0 36px` — it takes a FIXED width and never stretches,
               so View / Check in / Change keep the widths they have today and
               no label starts wrapping on a 375pt phone.
            ★★★ `Ban`, NOT `X`, AND THE REASON IS WHAT THE ACTION DOES. It
                revokes the share code and takes the case BACK from the doctor —
                the case is not deleted, it returns to `ready_to_share` and can
                go to somebody else. A trash can would promise deletion and a
                calendar-X would name only the appointment; both would be lying
                about the same button. **And a bare X on a CARD reads as
                "dismiss this card"** — this card collapses, so that reading is
                available and wrong. A circle-slash means cancel and nothing
                else, and its enclosed circle echoes the Check-in tick beside it.
            ★ Unlabelled, so the label is carried by aria-label AND title rather
              than by shape alone; it opens the same confirm dialog the labelled
              link opened, so nothing is destroyed by one tap. */}
        {onCancel && (
        <button
          type="button"
          aria-label={L.cancel}
          title={L.cancel}
          onClick={(e) => { e.stopPropagation(); onCancel() }}
          style={{ ...base, flex: '0 0 36px', padding: '9px 0', gap: 0, borderColor: '#FCA5A5' }}
          className="active:scale-[0.97] transition-transform"
        >
          <Ban size={14} strokeWidth={2.2} style={{ color: '#EF4444' }} />
        </button>
        )}
      </div>

      {showCheckIn && !eligible && !checkedIn && (
        <p style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '8px 0 0', fontSize: 11, color: '#9AA8B6' }}>
          <Clock size={12} style={{ color: '#B6C2CE', flexShrink: 0 }} />
          {L.closedNote}
        </p>
      )}

    </div>
  )
}
